import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  IntegrationInstructions as IntegrationIcon,
  Cloud as CloudIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Api as ApiIcon
} from '@mui/icons-material';

const Integration: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1B3B6F', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/')}
            sx={{ 
              color: 'white', 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Home
          </Button>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 700, mb: 2, color: 'white' }}>
            Secure Government Integration
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, color: 'white' }}>
            FedRAMP-Authorized Enterprise Integration Platform
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.95rem', mb: 3, maxWidth: '800px', lineHeight: 1.6 }}>
            Connect mission-critical systems with FedRAMP-authorized connectors, air-gapped deployment options, and full DISA STIG compliance.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#1B3B6F',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Key Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 6 }}>
          Integration Capabilities
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <ApiIcon sx={{ fontSize: 40 }} />,
              title: 'FedRAMP Connectors',
              desc: 'Pre-authorized connectors for common federal systems and databases'
            },
            {
              icon: <LockIcon sx={{ fontSize: 40 }} />,
              title: 'Air-Gapped Deployment',
              desc: 'Secure deployment options for classified and sensitive environments'
            },
            {
              icon: <CloudIcon sx={{ fontSize: 40 }} />,
              title: 'Hybrid Architecture',
              desc: 'Seamless integration between on-premise and cloud environments'
            },
            {
              icon: <IntegrationIcon sx={{ fontSize: 40 }} />,
              title: 'DISA STIG Compliance',
              desc: 'Full compliance with Defense Information Systems Agency security guidelines'
            }
          ].map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Box sx={{ color: '#1B3B6F', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Supported Systems */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 6 }}>
            Supported Systems & Platforms
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  Databases
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {['Oracle', 'SQL Server', 'PostgreSQL', 'MongoDB', 'DB2', 'Teradata'].map((db) => (
                    <Chip key={db} label={db} size="small" />
                  ))}
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  Cloud Platforms
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {['AWS GovCloud', 'Azure Government', 'Google Cloud', 'Oracle Cloud'].map((cloud) => (
                    <Chip key={cloud} label={cloud} size="small" />
                  ))}
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                  Enterprise Systems
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {['SAP', 'Salesforce', 'ServiceNow', 'Workday', 'SharePoint'].map((sys) => (
                    <Chip key={sys} label={sys} size="small" />
                  ))}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Security Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 6 }}>
          Security & Compliance
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                Security Features
              </Typography>
              <List>
                {[
                  'End-to-end encryption',
                  'Multi-factor authentication',
                  'Role-based access control',
                  'Audit logging and monitoring'
                ].map((item, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#1B3B6F' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 600 }}>
                Compliance Standards
              </Typography>
              <List>
                {[
                  'FedRAMP Moderate/High',
                  'DISA STIG compliant',
                  'NIST 800-53 controls',
                  'FISMA certified'
                ].map((item, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#1B3B6F' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 6 }}>
            Integration Benefits
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <List>
                {[
                  'Reduce integration time by 70%',
                  'Pre-built federal system connectors',
                  'Real-time data synchronization',
                  'Automated error handling and retry'
                ].map((item, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#1B3B6F' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item}
                      primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '0.95rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                {[
                  'Low-code integration builder',
                  'Comprehensive monitoring and alerts',
                  'Version control and rollback',
                  '24/7 federal support team'
                ].map((item, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#1B3B6F' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item}
                      primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '0.95rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Card sx={{ p: 6, textAlign: 'center', bgcolor: '#fff3e0' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#1B3B6F', fontWeight: 700 }}>
            Connect Your Mission-Critical Systems
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Secure, compliant integration for federal agencies
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: '#1B3B6F',
              color: 'white',
              px: 6,
              py: 2,
              '&:hover': { bgcolor: '#8B1515' }
            }}
          >
            Get Started Today
          </Button>
        </Card>
      </Container>
    </Box>
  );
};

export default Integration;
